import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FlaskConical, Leaf, RotateCcw, Sprout, Truck } from 'lucide-react';
import { ShopProductCard } from '../../ShopProductCard';
import { ReviewsSummary } from '../../ReviewsSummary';
import { useCart } from '../../../contexts/CartContext';
import { useBestSellers } from '../../../utils/useBestSellers';
import { useReviewStats } from '../../../utils/useReviewStats';
import { useCatalogProducts } from '../../../utils/useCatalogProducts';
import { getStorefrontProducts } from '../../../utils/catalog';
import { subscribeToNewsletter } from '../../../utils/firebase';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/jZM2tEohB8P3RXVY53wDZo/file_0000000050e471fd9b72564486af2c12.png';
const COLLECTIONS: {
  name: string;
  blurb: string;
  query: string;
}[] = [{
  name: 'Protein',
  blurb: 'Whey isolate and daily protein support',
  query: 'protein'
}, {
  name: 'Strength',
  blurb: 'Creatine and training-day essentials',
  query: 'strength'
}, {
  name: 'Endurance',
  blurb: 'Electrolytes and hydration',
  query: 'endurance'
}, {
  name: 'Health',
  blurb: 'Vitamins, omega-3, and daily foundations',
  query: 'health'
}, {
  name: 'Stress',
  blurb: 'Adaptogens and recovery support',
  query: 'stress'
}, {
  name: 'Sleep',
  blurb: 'Magnesium and evening wind-down',
  query: 'sleep'
}];
export function Home() {
  const {
    addItem
  } = useCart();
  const catalogProducts = useCatalogProducts();
  const storefrontProducts = getStorefrontProducts(catalogProducts);
  const bestSellers = useBestSellers();
  const reviewStats = useReviewStats();
  const featured = [...storefrontProducts.filter(product => bestSellers.has(product.id)), ...storefrontProducts.filter(product => !bestSellers.has(product.id))].slice(0, 6);
  return <div className="w-full min-h-screen bg-white" data-id="element-2475" data-mp="s3ohqiumo52f">
      {/* Hero */}
      <section className="relative min-h-[520px] md:h-[560px] px-6 pt-[91px] overflow-hidden" data-id="element-2476" data-mp="2tskk7e0mklta">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url('${HERO_IMAGE}')`
      }} data-id="element-2477" data-mp="2othersgqgdks" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/40 pointer-events-none" data-id="element-2478" data-mp="2k1pozk5eibvp" />

        <div className="relative h-full max-w-7xl mx-auto flex items-center" data-id="element-2479" data-mp="2g0nufgmfio02">
          <div className="w-full py-12 md:py-0" data-id="element-2480" data-mp="2b34b42avjl19">
            <p className="text-[10px] md:text-[11px] font-bold text-[#E8E4D8]/90 uppercase tracking-[0.25em] mb-3" data-id="element-2481" data-mp="2azq7yrudz7sr">
              Science-backed. Dietitian approved.
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white leading-[1.05] mb-1 max-w-2xl" data-id="element-2482" data-mp="215zjvo268zdl">
              Clean supplements,
            </h1>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#E8E4D8] italic leading-[1.05] mb-5 max-w-2xl" data-id="element-2483" data-mp="3vgwhnkfjz6r8">
              nothing to hide.
            </h1>
            <p className="text-sm md:text-base text-white/80 mb-7 max-w-lg leading-relaxed" data-id="element-2484" data-mp="1d939ki9uwskh">
              Every formula is built on published research, made with premium
              ingredients, and shipped free on orders over $99.
            </p>
            <div className="flex flex-wrap gap-3" data-id="element-2485" data-mp="1mz6ar5r4tctw">
              <Link to="/shop" className="inline-flex px-7 py-3.5 bg-tealslate text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#4A5A5F] transition-colors" data-id="element-2486" data-mp="1r0hrsmvxwpqh">
                Shop All Supplements
              </Link>
              <Link to="/shop?search=bestseller" className="inline-flex px-7 py-3.5 border border-white/40 text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-white/10 transition-colors" data-id="element-2487" data-mp="13adq4l2bb024">
                Shop Best Sellers
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-4 max-w-lg mt-8" data-id="element-2488" data-mp="2jo44ckn5b4ph">
              {[{
              icon: Leaf,
              label: 'Dietitian Formulated'
            }, {
              icon: FlaskConical,
              label: 'Premium Ingredients'
            }, {
              icon: Sprout,
              label: 'No Artificial Fillers'
            }].map(badge => <div key={badge.label} className="flex items-center gap-2 text-left" data-id="element-2489" data-mp="2r97udfzw49ct">
                  <badge.icon className="h-4 w-4 shrink-0 text-[#E8E4D8]" data-id="element-2490" data-mp="2n8ns5y0gmfjt" />
                  <span className="text-[10px] text-white/85 leading-tight" data-id="element-2491" data-mp="314rlzq7f8lvr">
                    {badge.label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Best sellers */}
      <section className="py-14 md:py-20 px-6" data-id="element-2492" data-mp="35wj4rekc5nbc">
        <div className="max-w-7xl mx-auto" data-id="element-2493" data-mp="35xuedv0gppad">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10" data-id="element-2494" data-mp="3jwaolvxj9xx9">
            <div data-id="element-2495" data-mp="3exr29uf12k4d">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1F1F1F] tracking-tight mb-2" data-id="element-2496" data-mp="3ey92t38q3ki3">
                Best sellers
              </h2>
              <p className="text-base text-[#5A5A5A]" data-id="element-2497" data-mp="2exzoewzlhr0">
                The formulas customers restock every month.
              </p>
            </div>
            <Link to="/shop" className="inline-flex items-center gap-2 text-sm font-bold text-[#1F1F1F] hover:text-tealslate transition-colors" data-id="element-2498" data-mp="3pjdx2zwlft1e">
              Shop all products <ArrowRight className="w-4 h-4" data-id="element-2499" data-mp="3fsrm5iztnqp1" />
            </Link>
          </div>

          {featured.length === 0 ? <div className="border border-dashed border-[#D8D8D0] bg-[#FAFAFA] px-6 py-14 text-center" data-id="element-2500" data-mp="3kkjfu2e9ycje">
              <h3 className="text-lg font-bold text-[#1F1F1F]" data-id="element-2501" data-mp="35w6jh4b9yrod">
                The shelves are being restocked
              </h3>
              <p className="text-sm text-[#5A5A5A] mt-2" data-id="element-2502" data-mp="3arlrvgqijvde">
                Products are loading, or the catalog is briefly unavailable.
                Check the full shop for the latest availability.
              </p>
              <Link to="/shop" className="inline-flex mt-6 px-5 py-2.5 bg-[#1F1F1F] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#3B3B3B] transition-colors rounded" data-id="element-2503" data-mp="17d1pkmf9eoku">
                Go to shop
              </Link>
            </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2504" data-mp="tm6m5pnj4zon">
              {featured.map(product => <ShopProductCard key={product.id} product={product} addItem={addItem} isBestseller={bestSellers.has(product.id)} reviewStats={reviewStats[product.id]} data-id="element-2505" data-mp="1qame1se43l4x" />)}
            </div>}
        </div>
      </section>

      {/* Shop by goal */}
      <section className="py-14 md:py-20 px-6 bg-[#F9F9F9] border-y border-[#E5E5E5]" data-id="element-2506" data-mp="5wlsoctr2k14">
        <div className="max-w-7xl mx-auto" data-id="element-2507" data-mp="x97dwij1l0n">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1F1F1F] tracking-tight mb-2" data-id="element-2508" data-mp="vejc9bylcsc">
            Shop by goal
          </h2>
          <p className="text-base text-[#5A5A5A] mb-10" data-id="element-2509" data-mp="2cii3jpk2kfm9">
            Six collections, built around what you're training for.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2510" data-mp="2lkrcwc3lq2y2">
            {COLLECTIONS.map(collection => <Link key={collection.name} to={`/shop?search=${collection.query}`} className="group flex items-center justify-between gap-4 rounded-2xl border border-[#E5E5E5] bg-white px-6 py-6 transition-[background-color,box-shadow] duration-200 ease-out hover:bg-[#E5E9F1] hover:shadow-lg" data-id="element-2511" data-mp="2qd0w79a7o4rd">
                <span data-id="element-2512" data-mp="39avdealhtmku">
                  <span className="block text-lg font-bold text-[#1F1F1F] mb-1" data-id="element-2513" data-mp="399akepetubp3">
                    {collection.name}
                  </span>
                  <span className="block text-sm text-[#5A5A5A]" data-id="element-2514" data-mp="1ewko610zkqej">
                    {collection.blurb}
                  </span>
                </span>
                <ArrowRight className="w-5 h-5 shrink-0 text-[#9A9A90] transition-colors group-hover:text-[#1F1F1F]" data-id="element-2515" data-mp="vz0zjcnt3kmf" />
              </Link>)}
          </div>
        </div>
      </section>

      {/* Store promises */}
      <section className="py-14 md:py-20 px-6" data-id="element-2516" data-mp="14uqg98eb7qth">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2517" data-mp="10qsgcvh5ubzd">
          {[{
          icon: Truck,
          title: 'Free shipping over $99',
          copy: 'Orders ship from the US within 1–2 business days, tracking included.'
        }, {
          icon: RotateCcw,
          title: '30-day returns',
          copy: "Try it. If a product isn't right for you, send it back within 30 days."
        }, {
          icon: FlaskConical,
          title: 'Tested, not guessed',
          copy: 'Third-party tested batches, clinically studied doses, no proprietary blends.'
        }].map(promise => <div key={promise.title} data-id="element-2518" data-mp="20yfqhc853l3o">
              <promise.icon className="w-6 h-6 text-tealslate mb-4" strokeWidth={1.75} data-id="element-2519" data-mp="211ld8ktaqaw8" />
              <h3 className="text-lg font-bold text-[#1F1F1F] mb-2" data-id="element-2520" data-mp="1r1uj4k8ndxwo">
                {promise.title}
              </h3>
              <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2521" data-mp="1w0xcv8j6ghpc">
                {promise.copy}
              </p>
            </div>)}
        </div>
      </section>

      <ReviewsSummary data-id="element-2522" data-mp="2opbfk1l7o278" />

      <NewsletterSignup data-id="element-2523" data-mp="2oni66ej7r3vj" />
    </div>;
}
function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus('loading');
    setMessage('');
    try {
      await subscribeToNewsletter(email, 'home');
      setStatus('done');
      setEmail('');
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    }
  };
  return <section className="py-16 md:py-20 px-6 bg-white border-t border-[#E5E5E5]" data-id="element-2524" data-mp="2d1i2xmnokhh7">
      <div className="max-w-2xl mx-auto text-center" data-id="element-2525" data-mp="2i0uycvsmawve">
        <h2 className="text-2xl md:text-3xl font-bold text-[#1F1F1F] tracking-tight mb-3" data-id="element-2526" data-mp="2i3qssa59b3f3">
          Restock reminders and new drops
        </h2>
        <p className="text-base text-[#5A5A5A] mb-8" data-id="element-2527" data-mp="283q7iintud89">
          One email when something launches or comes back in stock. Nothing else.
        </p>

        {status === 'done' ? <p className="text-base font-bold text-tealslate" data-id="element-2528" data-mp="1yddnqsnu6o36">
            You're on the list. Check your inbox for a confirmation.
          </p> : <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-id="element-2529" data-mp="1kenr1e4xiqfc">
            <label htmlFor="newsletter-email" className="sr-only" data-id="element-2530" data-mp="15qx5kcs4i6cg">
              Email address
            </label>
            <input id="newsletter-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@example.com" className="flex-1 bg-white border border-[#E5E5E5] px-4 py-3.5 text-[#1F1F1F] rounded-md focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-colors" data-id="element-2531" data-mp="10svqe2xtir69" />
            <button type="submit" disabled={status === 'loading'} className="px-6 py-3.5 bg-[#1F1F1F] text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#3B3B3B] transition-colors rounded disabled:opacity-50" data-id="element-2532" data-mp="1jovkbjacw4sh">
              {status === 'loading' ? 'Joining…' : 'Notify Me'}
            </button>
          </form>}

        {status === 'error' && <p className="text-sm text-burgundy font-bold mt-4" data-id="element-2533" data-mp="2aidw7y9nw6kd">{message}</p>}
      </div>
    </section>;
}